import type { InstrumentState, InstrumentType, WamParameterInfo } from '@/domain/types';
import type { AutomatableParam } from '../effects/EffectNode';
import type { TransportTell } from '../wamTransport';
import { defaultInstrumentParams } from './descriptors';

/**
 * An instrument turns note events into sound.
 *
 * Two entry points, because there are two callers with different knowledge:
 *
 *  - `playNote` is used by the sequencer, which knows a note's full length
 *    ahead of time and schedules it in one call at an exact context time.
 *  - `noteOn` / `noteOff` are used by live input, where the length is not known
 *    until the key comes up.
 *
 * Every time argument is an `AudioContext` time. Instruments never read the
 * clock themselves and never respond to "now" — like everything else in the
 * engine, they are told exactly when.
 */
export interface InstrumentNode {
  readonly type: InstrumentType;
  readonly output: AudioNode;

  /** Schedules a complete note. `duration` is in seconds of context time. */
  playNote(pitch: number, velocity: number, when: number, duration: number): void;

  noteOn(pitch: number, velocity: number, when: number): void;
  noteOff(pitch: number, when: number): void;
  /** Silences everything, e.g. when the transport stops mid-note. */
  allNotesOff(when: number): void;

  setParam(paramId: string, value: number, atTime?: number): void;
  paramTargets(paramId: string): AutomatableParam[];
  /**
   * Hands the instrument its structured state — the sampler's zone map, and
   * whatever a future instrument keeps there. Called whenever the project
   * changes, so an edit reaches the instrument without rebuilding it.
   */
  setState(state: InstrumentState): void;
  /**
   * For a hosted plugin: settles once it has loaded, what it said about
   * itself, and its own interface. Absent on the instruments built here,
   * which are ready the moment they exist.
   */
  ready?: Promise<void>;
  wamInfo?: Promise<{ name: string; parameters: WamParameterInfo[] }>;
  createGui?(): Promise<Element>;
  destroyGui?(gui: Element): void;
  /** For a hosted plugin: its whole state as JSON, or null while it is not loaded. */
  captureState?(): Promise<unknown>;
  /** For a hosted plugin: the transport, as `wam-transport` events on the plugin's node. */
  tellTransport?(tells: readonly TransportTell[]): void;
  /** For a hosted plugin: how late its sound arrives, in seconds; nothing until it loads. */
  latencySec?(): number;
  dispose(): void;
}

/**
 * How an instrument gets at decoded audio.
 *
 * It is a synchronous peek, not a promise: notes are scheduled on the audio
 * clock and cannot wait for I/O. A sampler zone whose audio is not resident
 * yet simply does not sound, and the caller is expected to have loaded what it
 * needs — which the offline renderer does explicitly, and live playback does
 * through the asset cache.
 */
export type BufferResolver = (assetId: string) => AudioBuffer | null;

export type InstrumentFactory = (ctx: BaseAudioContext, resolveBuffer: BufferResolver, state?: InstrumentState) => InstrumentNode;

const factories = new Map<InstrumentType, InstrumentFactory>();

export function registerInstrument(type: InstrumentType, factory: InstrumentFactory): void {
  factories.set(type, factory);
}

const noBuffers: BufferResolver = () => null;

export function createInstrumentNode(
  ctx: BaseAudioContext,
  state: InstrumentState,
  resolveBuffer: BufferResolver = noBuffers,
): InstrumentNode | null {
  const factory = factories.get(state.type);
  if (!factory) return null;
  const node = factory(ctx, resolveBuffer, state);
  const params = { ...defaultInstrumentParams(state.type), ...state.params };
  for (const [id, value] of Object.entries(params)) node.setParam(id, value, ctx.currentTime);
  node.setState(state);
  return node;
}

/** Shared helper: a short ramp rather than a step, so parameter moves never click. */
/**
 * How far the mod wheel opens the filter: three octaves above wherever the
 * cutoff sits. It rides on the filter's `detune`, which is in cents and
 * multiplies the frequency, so it composes with the cutoff control, the
 * cutoff's automation and the filter envelope instead of fighting any of them.
 */
export const MOD_WHEEL_CENTS = 3600;

export function rampParam(param: AudioParam, value: number, ctx: BaseAudioContext, atTime?: number): void {
  const t = atTime ?? ctx.currentTime;
  const safe = Number.isFinite(value) ? value : 0;
  try {
    param.setTargetAtTime(safe, t, 0.02);
  } catch {
    param.value = safe;
  }
}

/**
 * One second of white noise, generated once per context and shared by every
 * voice that needs it. Allocating a fresh noise buffer per drum hit would churn
 * megabytes a bar.
 */
const noiseBuffers = new WeakMap<BaseAudioContext, AudioBuffer>();

export function noiseBuffer(ctx: BaseAudioContext): AudioBuffer {
  const cached = noiseBuffers.get(ctx);
  if (cached) return cached;

  const length = Math.floor(ctx.sampleRate);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  noiseBuffers.set(ctx, buffer);
  return buffer;
}
